/**
 * Page-based live tours — each page has its own step sequence targeting
 * [data-tour-id="..."] on real routes. Roles: user | tenant_admin | admin
 */

import { USER_PAGE_TOURS_DEEP } from "./tours/userDeep";
import { TENANT_PAGE_TOURS_DEEP } from "./tours/tenantDeep";
import { ADMIN_PAGE_TOURS_DEEP } from "./tours/adminDeep";

export const ELEMENT_TYPES = {
  nav: { label: "Navigation", color: "#6366f1" },
  button: { label: "Action button", color: "#2563eb" },
  card: { label: "Module card", color: "#0ea5e9" },
  panel: { label: "Content panel", color: "#8b5cf6" },
  stat: { label: "Stat / metric", color: "#10b981" },
  field: { label: "Form field", color: "#f59e0b" },
  tab: { label: "Tab / filter", color: "#ec4899" },
  table: { label: "Data table", color: "#64748b" },
  module: { label: "Module section", color: "#475569" },
};

const USER_PAGE_TOURS = USER_PAGE_TOURS_DEEP;
const TENANT_PAGE_TOURS = TENANT_PAGE_TOURS_DEEP;
const ADMIN_PAGE_TOURS = ADMIN_PAGE_TOURS_DEEP;

/** Page tour id → license module key (omit = always visible). */
const PAGE_TOUR_MODULE = {
  "user-gap": "gap",
  "user-ropa": "ropa",
  "user-pia": "dpia",
  "user-tprm": "tprm",
  "user-breach": "breach",
  "user-consent": "consent",
  "user-policy": "policy_store",
  "user-reports": "reports",
  "tenant-gap": "gap",
  "tenant-pia": "dpia",
  "tenant-tprm": "tprm",
};

/** Page tour visible when the tenant has any of these modules. */
const PAGE_TOUR_ANY_MODULE = {
  "tenant-forms": ["gap", "ropa", "dpia", "tprm"],
  "tenant-responses": ["gap", "ropa", "dpia", "tprm"],
};

/** Individual live-tour step targets tied to a licensed module (e.g. dashboard cards). */
const STEP_TARGET_MODULE = {
  "tour-dash-consent": "consent",
  "tour-dash-cookie": "consent",
  "tour-dash-dsar": "dsar",
  "tour-dash-ropa": "ropa",
  "tour-dash-gap": "gap",
  "tour-dash-dspm": "dspm",
  "tour-dash-pia": "dpia",
  "tour-dash-tprm": "tprm",
  "tour-dash-qa-gap": "gap",
  "tour-dash-qa-ropa": "ropa",
  "tour-dash-qa-pia": "dpia",
  "tour-dash-qa-tprm": "tprm",
  "tour-dash-qa-reports": "reports",
};

function moduleAllowed(requiredModule, allowedModules, role) {
  if (role === "admin") return true;
  if (!requiredModule) return true;
  const keys = Array.isArray(requiredModule) ? requiredModule : [requiredModule];
  return keys.some((key) => (allowedModules || []).includes(key));
}

function filterPageTour(pageTour, allowedModules, role) {
  const pageMod = PAGE_TOUR_MODULE[pageTour.id];
  const anyMods = PAGE_TOUR_ANY_MODULE[pageTour.id];
  if (pageMod && !moduleAllowed(pageMod, allowedModules, role)) return null;
  if (anyMods && !moduleAllowed(anyMods, allowedModules, role)) return null;

  const steps = pageTour.steps.filter((step) => {
    const stepMod = step.requiredModule || STEP_TARGET_MODULE[step.targetId];
    return moduleAllowed(stepMod, allowedModules, role);
  });
  if (!steps.length) return null;
  return { ...pageTour, steps };
}

function filteredToursForRole(role, allowedModules) {
  return toursForRole(role)
    .map((pageTour) => filterPageTour(pageTour, allowedModules, role))
    .filter(Boolean);
}

const HELP_FINISH_STEP = {
  id: "help-finish",
  pageId: "help",
  pageTitle: "Help Guide",
  route: "/help",
  targetId: "nav-help",
  title: "Help & replay tours",
  elementType: "nav",
  size: "compact",
  placement: "bottom",
  body: "Return here anytime for documentation and page-by-page live tours.",
  action: "Open Help → Interactive Tour → pick any module card to replay that page.",
  outcome: "New users can self-serve onboarding anytime.",
};

function enrichSteps(pageTour) {
  return pageTour.steps.map((s, i) => ({
    ...s,
    id: s.id || `${pageTour.id}-${i}`,
    pageId: pageTour.id,
    pageTitle: pageTour.title,
    route: s.route || pageTour.route,
  }));
}

function toursForRole(role) {
  if (role === "tenant_admin") return TENANT_PAGE_TOURS;
  if (role === "admin") return ADMIN_PAGE_TOURS;
  return USER_PAGE_TOURS;
}

export function getPageTours(role, allowedModules = null) {
  const pages =
    allowedModules != null ? filteredToursForRole(role, allowedModules) : toursForRole(role);
  return pages.map((p) => ({
    id: p.id,
    title: p.title,
    route: p.route,
    description: p.description,
    stepCount: p.steps.length,
  }));
}

export function getPageTourSteps(pageId, role, allowedModules = null) {
  const pages =
    allowedModules != null ? filteredToursForRole(role, allowedModules) : toursForRole(role);
  const tour = pages.find((p) => p.id === pageId);
  if (!tour) return [];
  return enrichSteps(tour);
}

export function getFullTourSteps(role, allowedModules = null) {
  const pages =
    allowedModules != null ? filteredToursForRole(role, allowedModules) : toursForRole(role);
  const flat = pages.flatMap((p) => enrichSteps(p));
  return [...flat, { ...HELP_FINISH_STEP }];
}

export function getLiveTourSteps(role, allowedModules = null) {
  return getFullTourSteps(role, allowedModules);
}

export const LIVE_TOUR_CHAPTERS = [
  { id: "pages", title: "Module pages" },
  { id: "finish", title: "Finish" },
];
